import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'

export const CHECKLIST_TOGGLE_ACTION_ID = 'checklist.node.toggle-done'
export const CHECKLIST_NODE_ADD_CHILD_ACTION_ID = 'checklist.node.add-child'
export const CHECKLIST_NODE_LINK_ACTION_ID = 'checklist.node.link'
export const CHECKLIST_NODE_SHOW_BLOCKERS_ACTION_ID = 'checklist.node.show-blockers'
export const CHECKLIST_NODE_DELETE_ACTION_ID = 'checklist.node.delete'

export const CHECKLIST_GATE_TOGGLE_ACTION_ID = 'checklist.gate.toggle'
export const CHECKLIST_GATE_ADD_INPUT_ACTION_ID = 'checklist.gate.add-input'
export const CHECKLIST_GATE_SHOW_INPUTS_ACTION_ID = 'checklist.gate.show-inputs'
export const CHECKLIST_GATE_DELETE_ACTION_ID = 'checklist.gate.delete'

export const CHECKLIST_BOOK_RENAME_ACTION_ID = 'checklist.book.rename'
export const CHECKLIST_BOOK_DUPLICATE_ACTION_ID = 'checklist.book.duplicate'
export const CHECKLIST_BOOK_EXPORT_ACTION_ID = 'checklist.book.export'
export const CHECKLIST_BOOK_DELETE_ACTION_ID = 'checklist.book.delete'

export const CHECKLIST_PAGE_RENAME_ACTION_ID = 'checklist.page.rename'
export const CHECKLIST_PAGE_DUPLICATE_ACTION_ID = 'checklist.page.duplicate'
export const CHECKLIST_PAGE_MOVE_ACTION_ID = 'checklist.page.move'
export const CHECKLIST_PAGE_DELETE_ACTION_ID = 'checklist.page.delete'

export const CHECKLIST_CANVAS_NEW_TASK_ACTION_ID = 'checklist.canvas.new-task'
export const CHECKLIST_CANVAS_NEW_GATE_ACTION_ID = 'checklist.canvas.new-gate'
export const CHECKLIST_CANVAS_SORT_ACTION_ID = 'checklist.canvas.sort'
export const CHECKLIST_CANVAS_SHOW_BLOCKERS_ACTION_ID = 'checklist.canvas.show-blockers'

type ChecklistCandidate = { id: string; title: string }
type ChecklistToggleArgs = {
  prompt?: string
  selectedChecklistTargets?: ChecklistCandidate[]
  checklistCandidates?: ChecklistCandidate[]
  id?: string
  title?: string
}

export type ChecklistOrbitTargetArgs = {
  id?: string
  title?: string
}

export type ChecklistToggleConfirmPayload = {
  kind: 'CHECKLIST_TOGGLE_DONE'
  action: 'DONE' | 'UNDONE'
  targetIds: string[]
}

const candidateSchema: JSONSchemaType<ChecklistCandidate> = {
  type: 'object',
  properties: { id: { type: 'string' }, title: { type: 'string' } },
  required: ['id', 'title'],
  additionalProperties: false,
}

const checklistToggleArgsSchema: JSONSchemaType<ChecklistToggleArgs> = {
  type: 'object',
  properties: {
    prompt: { type: 'string', nullable: true },
    selectedChecklistTargets: { type: 'array', items: candidateSchema, nullable: true },
    checklistCandidates: { type: 'array', items: candidateSchema, nullable: true },
    id: { type: 'string', nullable: true },
    title: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: true,
}

const checklistOrbitTargetArgsSchema: JSONSchemaType<ChecklistOrbitTargetArgs> = {
  type: 'object',
  properties: {
    id: { type: 'string', nullable: true },
    title: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: true,
}

const parseIntent = (prompt: string): 'DONE' | 'UNDONE' => {
  if (/\b(uncheck|untick|reopen)\b/i.test(prompt)) return 'UNDONE'
  return 'DONE'
}

const TARGET_REQUIRED_ACTION_IDS = new Set([
  CHECKLIST_NODE_ADD_CHILD_ACTION_ID,
  CHECKLIST_NODE_LINK_ACTION_ID,
  CHECKLIST_NODE_SHOW_BLOCKERS_ACTION_ID,
  CHECKLIST_NODE_DELETE_ACTION_ID,
  CHECKLIST_GATE_TOGGLE_ACTION_ID,
  CHECKLIST_GATE_ADD_INPUT_ACTION_ID,
  CHECKLIST_GATE_SHOW_INPUTS_ACTION_ID,
  CHECKLIST_GATE_DELETE_ACTION_ID,
  CHECKLIST_BOOK_RENAME_ACTION_ID,
  CHECKLIST_BOOK_DUPLICATE_ACTION_ID,
  CHECKLIST_BOOK_EXPORT_ACTION_ID,
  CHECKLIST_BOOK_DELETE_ACTION_ID,
  CHECKLIST_PAGE_RENAME_ACTION_ID,
  CHECKLIST_PAGE_DUPLICATE_ACTION_ID,
  CHECKLIST_PAGE_MOVE_ACTION_ID,
  CHECKLIST_PAGE_DELETE_ACTION_ID,
])

const checklistToggleAction: ActionDefinition<ChecklistToggleArgs> = {
  id: CHECKLIST_TOGGLE_ACTION_ID,
  label: 'Toggle checklist item',
  description: 'Prepares selected checklist items for completion or reopening.',
  risk: 'MEDIUM',
  domain: 'checklist',
  surfaces: ['ai', 'orbit', 'catalog'],
  argsSchema: checklistToggleArgsSchema,
  permission: (context) => context.canPerform('checklist.change_status'),
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'

    if (args.id || context.invokedFrom === 'orbit') {
      if (!args.id) {
        const message = 'Target ID is required for Toggle checklist item'
        context.showToast?.(message, 'error')
        return {
          kind: 'FAILED',
          message,
          recoverable: false,
          correlationId,
        }
      }
      if (!context.checklistOps?.toggleDone) {
        const message = 'Operation for Toggle checklist item is not available'
        context.showToast?.(message, 'error')
        return {
          kind: 'FAILED',
          message,
          recoverable: false,
          correlationId,
        }
      }
      try {
        await Promise.resolve(context.checklistOps.toggleDone(args.id))
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to toggle checklist item'
        context.showToast?.(message, 'error')
        return {
          kind: 'FAILED',
          message,
          recoverable: false,
          correlationId,
        }
      }
      context.showToast?.(`Toggle done${args.title ? ` · ${args.title}` : ''}`, 'info')
      return {
        kind: 'COMPLETED',
        result: { id: args.id, title: args.title },
        message: 'Toggled done',
        correlationId,
      }
    }

    const prompt = args.prompt ?? ''
    const action = parseIntent(prompt)
    const selected = args.selectedChecklistTargets ?? []
    const candidates = selected.length > 0
      ? selected.slice(0, 5)
      : (args.checklistCandidates ?? []).slice(0, 5)
    if (candidates.length === 0) {
      return {
        kind: 'FAILED',
        message: `Select the checklist item you want to ${action === 'DONE' ? 'complete' : 'reopen'} and try again.`,
        recoverable: true,
        correlationId,
      }
    }
    const verb = action === 'DONE' ? 'Complete' : 'Reopen'
    const preview = candidates.map((candidate) => `• ${candidate.title}`).join('\n')
    const confirmPayload: ChecklistToggleConfirmPayload = {
      kind: 'CHECKLIST_TOGGLE_DONE',
      action,
      targetIds: candidates.map((candidate) => candidate.id),
    }
    return {
      kind: 'NEEDS_CONFIRMATION',
      message: `${verb}:\n${preview}\n\nConfirm?`,
      confirmPayload,
      correlationId,
    }
  },
}

const makeOrbitChecklistAction = (
  id: string,
  label: string,
  description: string,
  risk: 'READ_ONLY' | 'LOW' | 'MEDIUM' | 'HIGH',
  permissionAction?: 'checklist.create' | 'checklist.edit_structure' | 'checklist.delete' | 'checklist.edit_canvas' | 'timeline.view',
): ActionDefinition<ChecklistOrbitTargetArgs> => ({
  id,
  label,
  description,
  risk,
  domain: 'checklist',
  surfaces: ['orbit', 'catalog'],
  argsSchema: checklistOrbitTargetArgsSchema,
  permission: permissionAction ? (context) => context.canPerform(permissionAction) : undefined,
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'

    if (TARGET_REQUIRED_ACTION_IDS.has(id) && !args.id) {
      const message = `Target ID is required for ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    const ops = context.checklistOps
    let handler: (() => Promise<void> | void) | undefined

    if (id === CHECKLIST_NODE_ADD_CHILD_ACTION_ID) {
      handler = ops?.addChild ? () => ops.addChild!(args.id!) : undefined
    } else if (id === CHECKLIST_NODE_LINK_ACTION_ID) {
      handler = ops?.linkNode ? () => ops.linkNode!(args.id!) : undefined
    } else if (id === CHECKLIST_NODE_SHOW_BLOCKERS_ACTION_ID) {
      handler = ops?.showBlockers ? () => ops.showBlockers!(args.id!) : undefined
    } else if (id === CHECKLIST_NODE_DELETE_ACTION_ID) {
      handler = ops?.deleteNode ? () => ops.deleteNode!(args.id!) : undefined
    } else if (id === CHECKLIST_GATE_TOGGLE_ACTION_ID) {
      handler = ops?.toggleGate ? () => ops.toggleGate!(args.id!) : undefined
    } else if (id === CHECKLIST_GATE_ADD_INPUT_ACTION_ID) {
      handler = ops?.addGateInput ? () => ops.addGateInput!(args.id!) : undefined
    } else if (id === CHECKLIST_GATE_SHOW_INPUTS_ACTION_ID) {
      handler = ops?.showGateInputs ? () => ops.showGateInputs!(args.id!) : undefined
    } else if (id === CHECKLIST_GATE_DELETE_ACTION_ID) {
      handler = ops?.deleteGate ? () => ops.deleteGate!(args.id!) : undefined
    } else if (id === CHECKLIST_BOOK_RENAME_ACTION_ID) {
      handler = ops?.renameBook ? () => ops.renameBook!(args.id!) : undefined
    } else if (id === CHECKLIST_BOOK_DUPLICATE_ACTION_ID) {
      handler = ops?.duplicateBook ? () => ops.duplicateBook!(args.id!) : undefined
    } else if (id === CHECKLIST_BOOK_EXPORT_ACTION_ID) {
      handler = ops?.exportBook ? () => ops.exportBook!(args.id!) : undefined
    } else if (id === CHECKLIST_BOOK_DELETE_ACTION_ID) {
      handler = ops?.deleteBook ? () => ops.deleteBook!(args.id!) : undefined
    } else if (id === CHECKLIST_PAGE_RENAME_ACTION_ID) {
      handler = ops?.renamePage ? () => ops.renamePage!(args.id!) : undefined
    } else if (id === CHECKLIST_PAGE_DUPLICATE_ACTION_ID) {
      handler = ops?.duplicatePage ? () => ops.duplicatePage!(args.id!) : undefined
    } else if (id === CHECKLIST_PAGE_MOVE_ACTION_ID) {
      handler = ops?.movePage ? () => ops.movePage!(args.id!) : undefined
    } else if (id === CHECKLIST_PAGE_DELETE_ACTION_ID) {
      handler = ops?.deletePage ? () => ops.deletePage!(args.id!) : undefined
    } else if (id === CHECKLIST_CANVAS_NEW_TASK_ACTION_ID) {
      handler = ops?.createTask ? () => ops.createTask!() : undefined
    } else if (id === CHECKLIST_CANVAS_NEW_GATE_ACTION_ID) {
      handler = ops?.createGate ? () => ops.createGate!() : undefined
    } else if (id === CHECKLIST_CANVAS_SORT_ACTION_ID) {
      handler = ops?.sortCanvas ? () => ops.sortCanvas!() : undefined
    } else if (id === CHECKLIST_CANVAS_SHOW_BLOCKERS_ACTION_ID) {
      handler = ops?.showAllBlockers ? () => ops.showAllBlockers!() : undefined
    }

    if (!handler) {
      const message = `Operation for ${label} (${id}) is not available`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    try {
      await Promise.resolve(handler())
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to execute ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    context.showToast?.(`${label}${args.title ? ` · ${args.title}` : ''}`, 'info')
    return {
      kind: 'COMPLETED',
      result: { id: args.id, title: args.title },
      message: label,
      correlationId,
    }
  },
})

const checklistNodeAddChildAction = makeOrbitChecklistAction(
  CHECKLIST_NODE_ADD_CHILD_ACTION_ID,
  'Add child',
  'Adds a child checklist item under the selected node.',
  'LOW',
  'checklist.create',
)

const checklistNodeLinkAction = makeOrbitChecklistAction(
  CHECKLIST_NODE_LINK_ACTION_ID,
  'Link',
  'Links the checklist item to another node or dependency.',
  'LOW',
  'checklist.edit_structure',
)

const checklistNodeShowBlockersAction = makeOrbitChecklistAction(
  CHECKLIST_NODE_SHOW_BLOCKERS_ACTION_ID,
  'Show blockers',
  'Shows blocking dependencies for the selected checklist node.',
  'READ_ONLY',
  'timeline.view',
)

const checklistNodeDeleteAction = makeOrbitChecklistAction(
  CHECKLIST_NODE_DELETE_ACTION_ID,
  'Delete',
  'Deletes the selected checklist node.',
  'MEDIUM',
  'checklist.delete',
)

const checklistGateToggleAction = makeOrbitChecklistAction(
  CHECKLIST_GATE_TOGGLE_ACTION_ID,
  'Toggle AND/OR',
  'Toggles logic gate condition between AND and OR.',
  'LOW',
  'checklist.edit_structure',
)

const checklistGateAddInputAction = makeOrbitChecklistAction(
  CHECKLIST_GATE_ADD_INPUT_ACTION_ID,
  'Add input',
  'Adds a new dependency input to the gate.',
  'LOW',
  'checklist.create',
)

const checklistGateShowInputsAction = makeOrbitChecklistAction(
  CHECKLIST_GATE_SHOW_INPUTS_ACTION_ID,
  'Show inputs',
  'Displays all inputs connected to this gate.',
  'READ_ONLY',
  'timeline.view',
)

const checklistGateDeleteAction = makeOrbitChecklistAction(
  CHECKLIST_GATE_DELETE_ACTION_ID,
  'Delete',
  'Deletes the logic gate.',
  'MEDIUM',
  'checklist.delete',
)

const checklistBookRenameAction = makeOrbitChecklistAction(
  CHECKLIST_BOOK_RENAME_ACTION_ID,
  'Rename',
  'Renames the checklist book.',
  'LOW',
  'checklist.edit_structure',
)

const checklistBookDuplicateAction = makeOrbitChecklistAction(
  CHECKLIST_BOOK_DUPLICATE_ACTION_ID,
  'Duplicate',
  'Duplicates the checklist book.',
  'LOW',
  'checklist.edit_structure',
)

const checklistBookExportAction = makeOrbitChecklistAction(
  CHECKLIST_BOOK_EXPORT_ACTION_ID,
  'Export',
  'Exports the checklist book.',
  'READ_ONLY',
  'timeline.view',
)

const checklistBookDeleteAction = makeOrbitChecklistAction(
  CHECKLIST_BOOK_DELETE_ACTION_ID,
  'Delete',
  'Deletes the checklist book.',
  'HIGH',
  'checklist.delete',
)

const checklistPageRenameAction = makeOrbitChecklistAction(
  CHECKLIST_PAGE_RENAME_ACTION_ID,
  'Rename',
  'Renames the checklist page.',
  'LOW',
  'checklist.edit_structure',
)

const checklistPageDuplicateAction = makeOrbitChecklistAction(
  CHECKLIST_PAGE_DUPLICATE_ACTION_ID,
  'Duplicate',
  'Duplicates the checklist page.',
  'LOW',
  'checklist.edit_structure',
)

const checklistPageMoveAction = makeOrbitChecklistAction(
  CHECKLIST_PAGE_MOVE_ACTION_ID,
  'Move',
  'Moves the checklist page position.',
  'LOW',
  'checklist.edit_structure',
)

const checklistPageDeleteAction = makeOrbitChecklistAction(
  CHECKLIST_PAGE_DELETE_ACTION_ID,
  'Delete',
  'Deletes the checklist page.',
  'MEDIUM',
  'checklist.delete',
)

const checklistCanvasNewTaskAction = makeOrbitChecklistAction(
  CHECKLIST_CANVAS_NEW_TASK_ACTION_ID,
  'New task',
  'Creates a new checklist task on the canvas.',
  'LOW',
  'checklist.create',
)

const checklistCanvasNewGateAction = makeOrbitChecklistAction(
  CHECKLIST_CANVAS_NEW_GATE_ACTION_ID,
  'New gate',
  'Creates a new logic gate on the canvas.',
  'LOW',
  'checklist.create',
)

const checklistCanvasSortAction = makeOrbitChecklistAction(
  CHECKLIST_CANVAS_SORT_ACTION_ID,
  'Sort',
  'Sorts checklist nodes on the canvas.',
  'LOW',
  'checklist.edit_structure',
)

const checklistCanvasShowBlockersAction = makeOrbitChecklistAction(
  CHECKLIST_CANVAS_SHOW_BLOCKERS_ACTION_ID,
  'Show all blockers',
  'Shows all blocking dependencies across the canvas.',
  'READ_ONLY',
  'timeline.view',
)

export const registerChecklistActions = (): void => {
  if (!getAction(CHECKLIST_TOGGLE_ACTION_ID)) registerAction(checklistToggleAction)
  if (!getAction(CHECKLIST_NODE_ADD_CHILD_ACTION_ID)) registerAction(checklistNodeAddChildAction)
  if (!getAction(CHECKLIST_NODE_LINK_ACTION_ID)) registerAction(checklistNodeLinkAction)
  if (!getAction(CHECKLIST_NODE_SHOW_BLOCKERS_ACTION_ID)) registerAction(checklistNodeShowBlockersAction)
  if (!getAction(CHECKLIST_NODE_DELETE_ACTION_ID)) registerAction(checklistNodeDeleteAction)
  if (!getAction(CHECKLIST_GATE_TOGGLE_ACTION_ID)) registerAction(checklistGateToggleAction)
  if (!getAction(CHECKLIST_GATE_ADD_INPUT_ACTION_ID)) registerAction(checklistGateAddInputAction)
  if (!getAction(CHECKLIST_GATE_SHOW_INPUTS_ACTION_ID)) registerAction(checklistGateShowInputsAction)
  if (!getAction(CHECKLIST_GATE_DELETE_ACTION_ID)) registerAction(checklistGateDeleteAction)
  if (!getAction(CHECKLIST_BOOK_RENAME_ACTION_ID)) registerAction(checklistBookRenameAction)
  if (!getAction(CHECKLIST_BOOK_DUPLICATE_ACTION_ID)) registerAction(checklistBookDuplicateAction)
  if (!getAction(CHECKLIST_BOOK_EXPORT_ACTION_ID)) registerAction(checklistBookExportAction)
  if (!getAction(CHECKLIST_BOOK_DELETE_ACTION_ID)) registerAction(checklistBookDeleteAction)
  if (!getAction(CHECKLIST_PAGE_RENAME_ACTION_ID)) registerAction(checklistPageRenameAction)
  if (!getAction(CHECKLIST_PAGE_DUPLICATE_ACTION_ID)) registerAction(checklistPageDuplicateAction)
  if (!getAction(CHECKLIST_PAGE_MOVE_ACTION_ID)) registerAction(checklistPageMoveAction)
  if (!getAction(CHECKLIST_PAGE_DELETE_ACTION_ID)) registerAction(checklistPageDeleteAction)
  if (!getAction(CHECKLIST_CANVAS_NEW_TASK_ACTION_ID)) registerAction(checklistCanvasNewTaskAction)
  if (!getAction(CHECKLIST_CANVAS_NEW_GATE_ACTION_ID)) registerAction(checklistCanvasNewGateAction)
  if (!getAction(CHECKLIST_CANVAS_SORT_ACTION_ID)) registerAction(checklistCanvasSortAction)
  if (!getAction(CHECKLIST_CANVAS_SHOW_BLOCKERS_ACTION_ID)) registerAction(checklistCanvasShowBlockersAction)
}

export const registerChecklistToggleAction = (): void => {
  registerChecklistActions()
}

registerChecklistActions()
